import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { useAuth } from '../../context/AuthContext';
import PageWithCustomerSelector from '../PageWithCustomerSelector';
import DeliveryCreationStepper from './DeliveryCreationStepper';
import RequestersModal from './RequestersModal';
import ConfirmCancelDeliveryModal from './ConfirmCancelDeliveryModal';
import PickupAddressStep from './steps/PickupAddressStep';
import DeliveryAddressStep from './steps/DeliveryAddressStep';
import ItemsStep from './steps/ItemsStep';
import ValoresStep from './steps/ValoresStep';
import DeliveryRequesterSection from './steps/DeliveryRequesterSection';
import RevisaoStep from './steps/RevisaoStep';
import addressService from '../../services/addressService';
import deliveryService from '../../services/deliveryService';
import employeeService from '../../services/employeeService';
import {
    CUSTOMER_TYPE,
    isLegalPersonCustomer,
    isNaturalPersonCustomer,
    normalizeCustomerType,
} from '../../constants/customerType';
import legalPersonService from '../../services/legalPersonService';
import physicalPersonService from '../../services/physicalPersonService';
import {
    deliveryProtocolReportClient,
    prepareShareableReportFile,
    presentReportFile,
    sharePreparedFile,
} from '../../services/reportService';
import { CrudStyles } from '../shared/Crud';
import { mapAddressFromApi } from '../../utils/mapAddressFromApi';
import DeliveryStatusTimeline from './DeliveryStatusTimeline';
import DeliveryStatusUpdateSection from './DeliveryStatusUpdateSection';
import DeliveryDelayedBadge from './DeliveryDelayedBadge';
import DeliveryStatusBadge from './DeliveryStatusBadge';
import { TimelineSection, TimelineTitle, TimelineTitleRow } from './DeliveryStatusTimeline.styles';
import DeliveryAssociatedRoutesInfo from './DeliveryAssociatedRoutesInfo';
import DeliveryProtocolPrintModal from './DeliveryProtocolPrintModal';
import CopyDeliverySummaryButton from './CopyDeliverySummaryButton';
import { DELIVERY_STATUS } from './statusConfig';
import { maskCPF, maskPhone, maskZipCode } from '../../utils/masks';
import { normalizeDeliveryStateFromApi } from '../../utils/deliveryState';
import {
    numericIdOrNull,
    readDeliveryRequester,
    requesterInfoFromDto,
    saveDeliveryRequester,
} from '../../utils/deliveryRequesterStorage';
import { PrintIcon } from '../Layout/icons';
import MessageModal from '../shared/MessageModal';

const { Container, Button, FormContainer } = CrudStyles;

const STEPS_BASE = ['Endereço de coleta', 'Endereço de entrega', 'Itens'];

function normalizeApiDateTime(value) {
    if (!value) return '';
    const s = String(value).trim();
    if (!s) return '';
    return s.length >= 19 ? s.slice(0, 19) : s;
}

function mapPhoneNumberToDisplay(phoneDto) {
    if (!phoneDto || typeof phoneDto !== 'object') return '';
    const raw = phoneDto.number != null ? String(phoneDto.number) : '';
    if (!raw) return '';
    return maskPhone(raw.replace(/\D/g, ''));
}

function mapAddressToForm(addressDto) {
    const mapped = mapAddressFromApi(addressDto);
    if (!mapped) return null;
    return {
        ...mapped,
        zipCode: mapped.zipCode ? maskZipCode(String(mapped.zipCode)) : '',
    };
}

/**
 * Endereço do solicitante (GET /api/enderecos/:id/por-id) para preencher um endereço da entrega.
 * O id do endereço não é reaproveitado: a entrega sempre grava um endereço próprio.
 */
function mapRequesterAddressToForm(addressDto) {
    const mapped = mapAddressToForm(addressDto);
    if (!mapped) return null;
    return {
        zipCode: mapped.zipCode,
        street: mapped.street,
        number: mapped.number,
        complement: mapped.complement,
        district: mapped.district,
        city: mapped.city,
        stateAbbr: mapped.stateAbbr,
        stateName: mapped.stateName,
    };
}

function mapDeliveryItemToForm(item, index) {
    return {
        id: `item-${item?.id ?? index}-${Date.now()}`,
        deliveryItemId: item?.id ?? null,
        quantity: item?.quantity != null ? String(item.quantity) : '',
        height: item?.height != null ? String(item.height) : '',
        width: item?.width != null ? String(item.width) : '',
        length: item?.length != null ? String(item.length) : '',
        weight: item?.weight != null ? String(item.weight) : '',
        fragile: !!item?.fragile,
        price: item?.price != null ? Number(item.price) : null,
        description: item?.description ?? '',
        documentNumber: item?.externalId ?? '',
    };
}

/** Normaliza motorista retornado no GET da entrega (estrutura pode variar). */
function mapDriverFromDeliveryDto(dto) {
    if (!dto || typeof dto !== 'object') return { id: null, name: '' };
    const nested = dto.driver ?? dto.motorista ?? dto.deliveryDriver ?? dto.driverEmployee;
    if (nested && typeof nested === 'object') {
        const id = nested.id != null ? Number(nested.id) : null;
        const name = [nested.name, nested.fullName, nested.personName]
            .find((x) => x != null && String(x).trim() !== '');
        return {
            id: id != null && !Number.isNaN(id) ? id : null,
            name: name != null ? String(name).trim() : '',
        };
    }
    if (dto.driverId != null) {
        const id = Number(dto.driverId);
        return {
            id: !Number.isNaN(id) ? id : null,
            name: dto.driverName != null ? String(dto.driverName).trim() : '',
        };
    }
    return { id: null, name: '' };
}

/** Nome/documento/tipo do cliente da entrega quando o GET detalhado já os devolve. */
function deliveryCustomerFromDto(dto) {
    if (!dto || typeof dto !== 'object') return { name: '', document: '', type: null };
    const nested = dto.customer ?? dto.legalPerson ?? dto.physicalPerson ?? {};
    const person = nested.physicalPerson && typeof nested.physicalPerson === 'object'
        ? nested.physicalPerson
        : nested;
    const name = [
        dto.customerName,
        person.name,
        nested.name,
        dto.legalPersonName,
        nested.legalPersonName,
    ].find((x) => x != null && String(x).trim() !== '');
    const document = [
        dto.customerDocument,
        person.cpf,
        person.document,
        nested.document,
        nested.cpf,
        dto.legalPersonDocument,
        nested.cnpj,
    ].find((x) => x != null && String(x).trim() !== '');
    const type = normalizeCustomerType(
        dto.customerType ?? nested.customerType ?? nested.type ?? person.type ?? null
    );
    return {
        name: name != null ? String(name).trim() : '',
        document: document != null ? String(document).trim() : '',
        type,
    };
}

/** Nome e e-mail do solicitante no GET da entrega (PersonDTO em `requester`). */
function requesterDisplayFromDto(dto) {
    const info = requesterInfoFromDto(dto);
    const candidates = [
        dto?.requester,
        dto?.solicitante,
        dto?._embedded?.requester,
        dto?._embedded?.solicitante,
    ];
    const person = candidates.find((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate));
    const name = [person?.name, person?.fullName].find((value) => value != null && String(value).trim() !== '');
    const email = person?.email != null && String(person.email).trim() !== '' ? String(person.email).trim() : '';
    return {
        ...info,
        name: name != null ? String(name).trim() : '',
        email,
    };
}

function deliveryEntityCustomerIdFromDto(dto) {
    if (!dto || typeof dto !== 'object') return null;
    const raw = dto.customerId ?? dto.legalPersonId ?? dto.legalPersonCustomerId;
    if (raw == null || String(raw).trim() === '') return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
}

/** customerId exigido pelo PATCH de status: contexto do seletor ou o cliente da entrega no DTO. */
function customerIdForStatusPatch(deliveryCustomerId, deliveryEntityCustomerId) {
    if (deliveryCustomerId != null && String(deliveryCustomerId).trim() !== '') {
        return String(deliveryCustomerId).trim();
    }
    if (deliveryEntityCustomerId != null && String(deliveryEntityCustomerId).trim() !== '') {
        return String(deliveryEntityCustomerId).trim();
    }
    return null;
}

function mapDeliveryFeeToForm(fee, index) {
    const isPercentage = !!fee?.percentage;
    const calculationBasis = fee?.calculationBasis != null ? Number(fee.calculationBasis) : null;
    return {
        id: `fee-${fee?.id ?? index}-${Date.now()}`,
        feeId: fee?.id ?? null,
        description: fee?.description ?? '',
        value: fee?.amount != null ? Number(fee.amount) : null,
        quantity: isPercentage ? null : calculationBasis,
        isPercentage,
        savedSnapshot: {
            calculationBasis,
            amount: fee?.amount != null ? Number(fee.amount) : 0,
            description: fee?.description ?? '',
            percentage: isPercentage,
        },
    };
}

export default function NewDelivery() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const searchParams = new URLSearchParams(location.search || '');
    const idFromRoute = params?.id ?? params?.deliveryId ?? null;
    const idFromState = location.state?.deliveryId ?? null;
    const customerIdFromQuery = searchParams.get('customerId');
    const customerTypeFromQuery = normalizeCustomerType(searchParams.get('customerType'));
    const customerIdFromList = customerIdFromQuery ?? location.state?.deliveryCustomerId ?? null;
    const [deliveryId, setDeliveryId] = useState(idFromRoute ?? idFromState);
    // Solicitante da entrega: usado só para preencher os endereços sob demanda.
    const [requesterInfo, setRequesterInfo] = useState(() => {
        const stored = readDeliveryRequester(idFromRoute ?? idFromState);
        return {
            id: stored?.id ?? null,
            addressId: stored?.addressId ?? null,
            name: '',
            email: '',
        };
    });
    const [requesterFillTarget, setRequesterFillTarget] = useState(null);
    const { selectedCustomerId, selectedCustomerType, isManagingOwnData, isTransportCompany } =
        useCustomerSelector();
    // Tipo do cliente da entrega: navegação/query têm prioridade.
    // No detalhe, selectedCustomerType só vale se for o mesmo customerId (evita herdar PJ do seletor).
    // Na criação (sem customerId na URL), usa o tipo do cliente selecionado.
    const customerTypeFromList = useMemo(() => {
        const fromNav = normalizeCustomerType(
            customerTypeFromQuery ?? location.state?.deliveryCustomerType ?? null
        );
        if (fromNav) return fromNav;
        const deliveryCid = customerIdFromList != null ? String(customerIdFromList).trim() : '';
        const selectedCid = selectedCustomerId != null ? String(selectedCustomerId).trim() : '';
        const selectedType = normalizeCustomerType(selectedCustomerType);
        if (deliveryCid) {
            if (selectedCid && deliveryCid === selectedCid) return selectedType;
            return null;
        }
        return selectedType;
    }, [
        customerTypeFromQuery,
        location.state?.deliveryCustomerType,
        customerIdFromList,
        selectedCustomerId,
        selectedCustomerType,
    ]);
    const { user } = useAuth();
    const hideSelector = Boolean(deliveryId);

    const isLegalPersonUser = useMemo(
        () => (user?.roles || []).includes('ROLE_LEGAL_PERSON'),
        [user?.roles]
    );

    const canManageDeliveryPhotos = useMemo(() => {
        const roles = user?.roles || [];
        return roles.includes('ROLE_TRANSPORT_COMPANY') || roles.includes('ROLE_HR_EMPLOYEE');
    }, [user?.roles]);

    const canPrintDeliveryProtocol = useMemo(() => {
        const roles = user?.roles || [];
        return roles.includes('ROLE_TRANSPORT_COMPANY') || roles.includes('ROLE_HR_EMPLOYEE');
    }, [user?.roles]);

    const canViewAssociatedRoutes = useMemo(() => {
        const roles = user?.roles || [];
        return isTransportCompany || roles.includes('ROLE_HR_EMPLOYEE');
    }, [isTransportCompany, user?.roles]);

    const canEditBilling = useMemo(() => {
        const roles = user?.roles || [];
        return roles.includes('ROLE_TRANSPORT_COMPANY') || roles.includes('ROLE_HR_EMPLOYEE');
    }, [user?.roles]);

    useEffect(() => {
        const nextId = idFromRoute ?? idFromState ?? null;
        if (nextId != null) setDeliveryId(nextId);
    }, [idFromRoute, idFromState]);

    useEffect(() => {
        const stored = readDeliveryRequester(deliveryId);
        if (!stored) return;
        setRequesterInfo((prev) => ({
            ...prev,
            id: prev.id ?? stored.id,
            addressId: prev.addressId ?? stored.addressId,
        }));
    }, [deliveryId]);

    const [currentStep, setCurrentStep] = useState(0);
    const [creating, setCreating] = useState(false);
    const [hydratingDelivery, setHydratingDelivery] = useState(false);
    const [cancellingDelivery, setCancellingDelivery] = useState(false);
    const [showProtocolPrintModal, setShowProtocolPrintModal] = useState(false);
    const [protocolShareFile, setProtocolShareFile] = useState(null);
    const [printingProtocol, setPrintingProtocol] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const [formData, setFormData] = useState({
        pickupAddress: null,
        deliveryAddress: null,
        items: [],
        pickupDateTime: '',
        pickupInstructions: '',
        deliveryDateTime: '',
        deliveryInstructions: '',
        pickupAddressSaved: false,
        deliveryAddressSaved: false,
        deliveryValue: null,
        deliveryBillDescription: '',
        deliveryBillId: null,
        deliveryBillEditable: true,
        savedDeliveryValue: null,
        savedDeliveryBillDescription: null,
        fees: [],
        recipientId: null,
        recipientName: '',
        recipientDocument: '',
        recipientPhone: '',
        driverId: null,
        driverName: '',
        deliveryEntityCustomerId: null,
    });

    const [deliveryState, setDeliveryState] = useState('');
    const [deliveryDelayed, setDeliveryDelayed] = useState(false);
    const [deliveryCustomer, setDeliveryCustomer] = useState({
        name: '',
        document: '',
        type: customerTypeFromList,
    });
    const [deliveryHistory, setDeliveryHistory] = useState([]);
    const [deliveryRoutes, setDeliveryRoutes] = useState([]);
    const [refetchNonce, setRefetchNonce] = useState(0);

    // ÚNICO customerId da entrega: calculado uma vez aqui
    const deliveryCustomerId = useMemo(() => {
        if (!isTransportCompany) return undefined;

        // 1) Listagem geral (todas as empresas): usa SEMPRE o customerId do item
        if (customerIdFromList != null && String(customerIdFromList).trim() !== '') {
            return customerIdFromList;
        }

        // 2) Transportadora filtrando por um cliente específico (não “minha empresa”)
        if (!isManagingOwnData && selectedCustomerId != null && String(selectedCustomerId).trim() !== '') {
            return selectedCustomerId;
        }

        // 3) “Minha empresa” ou nada selecionado → não parametriza customerId
        return undefined;
    }, [isTransportCompany, isManagingOwnData, customerIdFromList, selectedCustomerId]);

    const statusPatchCustomerId = useMemo(
        () => customerIdForStatusPatch(deliveryCustomerId, formData.deliveryEntityCustomerId),
        [deliveryCustomerId, formData.deliveryEntityCustomerId]
    );

    const bumpDeliveryRefetch = useCallback(() => setRefetchNonce((n) => n + 1), []);

    const requesterAddressCustomerId = deliveryCustomerId ?? selectedCustomerId ?? null;
    const canFillFromRequester = requesterInfo.addressId != null || requesterInfo.id != null;

    // A consulta por-id só acontece no clique do botão; o CEP é preenchido sem disparar a busca por CEP.
    const fillAddressFromRequester = useCallback(async (target) => {
        setRequesterFillTarget(target);
        try {
            let addressId = requesterInfo.addressId;
            if (addressId == null && requesterInfo.id != null) {
                const employee = await employeeService.get(requesterInfo.id, requesterAddressCustomerId);
                addressId = numericIdOrNull(employee?.addressId);
                setRequesterInfo((prev) => ({ ...prev, addressId }));
            }
            if (addressId == null) {
                setMessageModal({
                    open: true,
                    title: 'Aviso',
                    message: 'O solicitante da entrega não possui endereço cadastrado.',
                });
                return;
            }
            const dto = await addressService.getById(addressId, requesterAddressCustomerId);
            const address = mapRequesterAddressToForm(dto);
            if (!address) {
                setMessageModal({
                    open: true,
                    title: 'Aviso',
                    message: 'Não foi possível obter o endereço do solicitante.',
                });
                return;
            }
            setFormData((d) => (target === 'pickup'
                ? { ...d, pickupAddress: address }
                : { ...d, deliveryAddress: address }));
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao buscar o endereço do solicitante.',
            });
        } finally {
            setRequesterFillTarget(null);
        }
    }, [requesterInfo.id, requesterInfo.addressId, requesterAddressCustomerId]);

    const STEPS = useMemo(
        () => (
            isTransportCompany || isLegalPersonUser
                ? [...STEPS_BASE, 'Detalhes da entrega', 'Revisão']
                : [...STEPS_BASE, 'Revisão']
        ),
        [isTransportCompany, isLegalPersonUser]
    );

    const handleRequesterUpdated = useCallback((requester) => {
        setRequesterInfo((prev) => {
            const next = {
                id: numericIdOrNull(requester?.id) ?? prev.id,
                // Endereço do solicitante anterior não pode permanecer depois da troca.
                addressId: numericIdOrNull(requester?.addressId),
                name: requester?.name != null ? String(requester.name) : (prev.name || ''),
                email: requester?.email != null ? String(requester.email) : (prev.email || ''),
            };
            saveDeliveryRequester(deliveryId, next);
            return next;
        });
        bumpDeliveryRefetch();
    }, [deliveryId, bumpDeliveryRefetch]);

    const handleCreateDelivery = async (payload, requesterFromSelection = null) => {
        setCreating(true);
        try {
            const { deliveryId: id } = await deliveryService.createDelivery(payload);
            if (id) {
                setDeliveryId(id);
                saveDeliveryRequester(id, requesterFromSelection ?? {
                    id: payload.requesterId ?? null,
                    addressId: null,
                });
                navigate(ROUTES.ENTREGA_NOVA, { state: { deliveryId: id }, replace: true });
            } else {
                setMessageModal({
                    open: true,
                    title: 'Aviso',
                    message: 'Não foi possível obter o ID da entrega criada.',
                });
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao criar entrega.',
            });
        } finally {
            setCreating(false);
        }
    };

    const handleRequesterSelect = (employee) => {
        if (!selectedCustomerId) return;
        const requester = {
            id: numericIdOrNull(employee.id),
            addressId: numericIdOrNull(employee.addressId),
            name: employee.name ?? '',
            email: employee.email ?? '',
        };
        setRequesterInfo(requester);
        handleCreateDelivery(
            {
                customerId: selectedCustomerId,
                requesterId: employee.id,
            },
            requester
        );
    };

    const goBack = () => {
        if (currentStep > 0) setCurrentStep((s) => s - 1);
    };

    useEffect(() => {
        if (!deliveryId) return;

        let cancelled = false;
        const hydrateDelivery = async () => {
            setHydratingDelivery(true);
            try {
                const dto = await deliveryService.getDeliveryById(deliveryId, deliveryCustomerId);
                if (cancelled || !dto) return;

                const rawState = dto.state;
                const stateVal =
                    typeof rawState === 'string'
                        ? rawState
                        : (rawState && (rawState.value || rawState.status)) || '';
                setDeliveryState(stateVal || '');
                setDeliveryDelayed(dto.delayed === true);
                setDeliveryCustomer((prev) => {
                    const fromDto = deliveryCustomerFromDto(dto);
                    return {
                        name: fromDto.name || prev.name,
                        document: fromDto.document || prev.document,
                        // Preserva tipo da listagem/query quando o GET da entrega não traz customerType.
                        type: fromDto.type || prev.type || customerTypeFromList,
                    };
                });
                const requesterFromDto = requesterDisplayFromDto(dto);
                setRequesterInfo((prev) => {
                    const next = {
                        id: requesterFromDto.id ?? prev.id,
                        addressId: prev.addressId ?? requesterFromDto.addressId,
                        name: requesterFromDto.name || prev.name || '',
                        email: requesterFromDto.email || prev.email || '',
                    };
                    saveDeliveryRequester(deliveryId, next);
                    return next;
                });
                setDeliveryHistory(Array.isArray(dto.history) ? dto.history : []);
                setDeliveryRoutes(Array.isArray(dto.routes) ? dto.routes : []);

                const pickupAddress = mapAddressToForm(dto.collectionAddress?.address);
                const deliveryAddress = mapAddressToForm(dto.deliveryAddress?.address);
                const receiver = dto.receiver || {};
                const responsible = dto.deliveryAddress?.responsible || {};
                const bill = dto.bill || {};
                const fees = Array.isArray(bill.fees) ? bill.fees.map(mapDeliveryFeeToForm) : [];
                const items = Array.isArray(dto.items) ? dto.items.map(mapDeliveryItemToForm) : [];
                const responsibleDocumentRaw = responsible.document != null ? String(responsible.document) : '';
                const receiverDocumentRaw = receiver.document != null ? String(receiver.document) : '';
                const recipientDocumentRaw = responsibleDocumentRaw || receiverDocumentRaw;
                const driverMapped = mapDriverFromDeliveryDto(dto);

                setFormData((prev) => ({
                    ...prev,
                    pickupAddress,
                    deliveryAddress,
                    pickupDateTime: normalizeApiDateTime(dto.collectionAddress?.dateTime),
                    pickupInstructions: dto.collectionAddress?.instructions ?? '',
                    deliveryDateTime: normalizeApiDateTime(dto.deliveryAddress?.dateTime),
                    deliveryInstructions: dto.deliveryAddress?.instructions ?? '',
                    pickupAddressSaved: !!pickupAddress,
                    deliveryAddressSaved: !!deliveryAddress,
                    items,
                    deliveryValue: bill.amount != null ? Number(bill.amount) : null,
                    deliveryBillDescription: bill.description ?? '',
                    deliveryBillId: bill.id ?? null,
                    deliveryBillEditable: bill.id == null ? true : Boolean(bill.editable),
                    savedDeliveryValue: bill.amount != null ? Number(bill.amount) : null,
                    savedDeliveryBillDescription: bill.description ?? '',
                    fees,
                    recipientId: responsible.id ?? null,
                    recipientName: responsible.name ?? receiver.name ?? '',
                    recipientDocument: recipientDocumentRaw ? maskCPF(recipientDocumentRaw.replace(/\D/g, '')) : '',
                    recipientPhone: mapPhoneNumberToDisplay(responsible.phoneNumber || receiver.phoneNumber),
                    driverId: driverMapped.id,
                    driverName: driverMapped.name,
                    deliveryEntityCustomerId: deliveryEntityCustomerIdFromDto(dto),
                }));
            } catch (err) {
                if (!cancelled) {
                    setMessageModal({
                        open: true,
                        title: 'Erro',
                        message: err?.message || 'Erro ao carregar dados da entrega para reabertura.',
                    });
                }
            } finally {
                if (!cancelled) {
                    setHydratingDelivery(false);
                }
            }
        };

        hydrateDelivery();
        return () => {
            cancelled = true;
        };
    }, [deliveryId, deliveryCustomerId, refetchNonce, customerTypeFromList]);

    // Nome/documento/tipo do cliente: complementa quando o GET da entrega não trouxe.
    // PJ: GET /api/pessoa-juridica/{id} · PF: GET /api/pessoa-fisica/cliente/{customerId}
    // Tipo desconhecido: tenta PF antes de PJ (evita tratar entrega PF como PJ).
    const customerIdForCustomerData = formData.deliveryEntityCustomerId ?? statusPatchCustomerId;
    const resolvedCustomerType = normalizeCustomerType(deliveryCustomer.type ?? customerTypeFromList);
    const customerDataFetchKeyRef = useRef(null);
    useEffect(() => {
        if (!isTransportCompany) return;
        if (customerIdForCustomerData == null || String(customerIdForCustomerData).trim() === '') return;

        const fetchKey = `${resolvedCustomerType ?? 'unknown'}:${customerIdForCustomerData}`;
        let cancelled = false;

        const applyLegalPerson = (dto) => {
            if (cancelled || !dto) return;
            setDeliveryCustomer((prev) => ({
                ...prev,
                name: prev.name || String(dto.name ?? '').trim(),
                document: prev.document || String(dto.document ?? '').trim(),
                type: CUSTOMER_TYPE.PESSOA_JURIDICA,
            }));
        };

        const applyPhysicalPerson = (dto) => {
            if (cancelled || !dto) return;
            const person = dto.physicalPerson ?? dto;
            const name = String(person?.name ?? dto?.name ?? '').trim();
            const document = String(person?.cpf ?? person?.document ?? dto?.cpf ?? dto?.document ?? '').trim();
            setDeliveryCustomer((prev) => ({
                ...prev,
                name: prev.name || name,
                document: prev.document || document,
                type: CUSTOMER_TYPE.PESSOA_FISICA,
            }));
        };

        if (isLegalPersonCustomer(resolvedCustomerType)) {
            if (deliveryCustomer.name && deliveryCustomer.document) return;
            if (customerDataFetchKeyRef.current === fetchKey) return;
            customerDataFetchKeyRef.current = fetchKey;
            legalPersonService
                .getById(customerIdForCustomerData)
                .then(applyLegalPerson)
                .catch(() => {});
            return () => {
                cancelled = true;
            };
        }

        if (isNaturalPersonCustomer(resolvedCustomerType)) {
            if (deliveryCustomer.name) return;
            if (customerDataFetchKeyRef.current === fetchKey) return;
            customerDataFetchKeyRef.current = fetchKey;
            physicalPersonService
                .getByCustomerId(customerIdForCustomerData)
                .then(applyPhysicalPerson)
                .catch(() => {});
            return () => {
                cancelled = true;
            };
        }

        // Tipo ainda desconhecido: descobrir PF/PJ pelo customerId antes de exibir centro de custo.
        if (customerDataFetchKeyRef.current === fetchKey) return;
        customerDataFetchKeyRef.current = fetchKey;
        (async () => {
            try {
                const pfDto = await physicalPersonService.getByCustomerId(customerIdForCustomerData);
                if (cancelled) return;
                if (pfDto && (pfDto.physicalPerson || pfDto.name || pfDto.id != null)) {
                    applyPhysicalPerson(pfDto);
                    return;
                }
            } catch {
                // Não é PF (ou sem permissão); tenta PJ.
            }
            if (cancelled) return;
            try {
                const pjDto = await legalPersonService.getById(customerIdForCustomerData);
                if (cancelled) return;
                applyLegalPerson(pjDto);
            } catch {
                // Dado complementar: falha não deve afetar a tela.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        isTransportCompany,
        resolvedCustomerType,
        customerIdForCustomerData,
        deliveryCustomer.name,
        deliveryCustomer.document,
    ]);

    const goNext = useCallback(() => {
        if (currentStep >= STEPS.length - 1) return;
        setCurrentStep((s) => s + 1);
    }, [currentStep, STEPS.length]);

    const handleConfirmCancel = async () => {
        if (deliveryId) {
            setCancellingDelivery(true);
            try {
                await deliveryService.cancelDelivery(deliveryId, deliveryCustomerId);
                setShowCancelModal(false);
                window.location.replace(ROUTES.ENTREGAS);
            } catch (err) {
                setMessageModal({
                    open: true,
                    title: 'Erro',
                    message: err?.message || 'Erro ao cancelar a entrega.',
                });
            } finally {
                setCancellingDelivery(false);
            }
            return;
        }
        setShowCancelModal(false);
        window.location.replace(ROUTES.ENTREGAS);
    };

    const handleOpenProtocolPrintModal = () => {
        if (!deliveryId) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'ID da entrega não encontrado.',
            });
            return;
        }

        const customerId = statusPatchCustomerId;
        if (customerId == null || String(customerId).trim() === '') {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Não foi possível identificar o cliente da entrega para emitir o protocolo.',
            });
            return;
        }

        setProtocolShareFile(null);
        setShowProtocolPrintModal(true);
    };

    const closeProtocolPrint = () => {
        setShowProtocolPrintModal(false);
        setProtocolShareFile(null);
    };

    const handleConfirmProtocolPrint = async (includeValue) => {
        if (!deliveryId) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'ID da entrega não encontrado.',
            });
            return;
        }

        const customerId = statusPatchCustomerId;
        if (customerId == null || String(customerId).trim() === '') {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Não foi possível identificar o cliente da entrega para emitir o protocolo.',
            });
            return;
        }

        setPrintingProtocol(true);
        try {
            const file = await deliveryProtocolReportClient.emit({
                deliveryId: Number(deliveryId),
                customerId: Number(customerId),
                includeValue: Boolean(includeValue),
            });
            const outcome = await presentReportFile(file);
            if (outcome === 'needs-gesture') {
                setProtocolShareFile(await prepareShareableReportFile(file));
                return;
            }
            closeProtocolPrint();
        } catch (err) {
            if (err?.name !== 'AbortError') {
                setMessageModal({
                    open: true,
                    title: 'Erro',
                    message: err?.message || 'Erro ao emitir protocolo de entrega.',
                });
            }
        } finally {
            setPrintingProtocol(false);
        }
    };

    const handleShareProtocol = () => {
        if (!protocolShareFile) return undefined;
        return sharePreparedFile(protocolShareFile)
            .then(() => closeProtocolPrint())
            .catch((err) => {
                if (err?.name === 'AbortError') return;
                setMessageModal({
                    open: true,
                    title: 'Erro',
                    message: 'Não foi possível abrir o compartilhamento. Tente novamente.',
                });
            });
    };

    const messageModalEl = messageModal.open ? (
        <MessageModal
            title={messageModal.title}
            message={messageModal.message}
            onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
        />
    ) : null;

    const protocolPrintModal = (
        <DeliveryProtocolPrintModal
            open={showProtocolPrintModal}
            isSubmitting={printingProtocol}
            shareFile={protocolShareFile}
            onClose={() => !printingProtocol && closeProtocolPrint()}
            onConfirm={handleConfirmProtocolPrint}
            onShare={handleShareProtocol}
        />
    );

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <PickupAddressStep
                        deliveryId={deliveryId}
                        customerIdForApi={deliveryCustomerId}
                        address={formData.pickupAddress}
                        onChange={(a) => setFormData((d) => ({ ...d, pickupAddress: a }))}
                        pickupDateTime={formData.pickupDateTime}
                        pickupInstructions={formData.pickupInstructions}
                        deliveryDateTime={formData.deliveryDateTime}
                        onPickupDateTimeChange={(v) => setFormData((d) => ({ ...d, pickupDateTime: v }))}
                        onPickupInstructionsChange={(v) => setFormData((d) => ({ ...d, pickupInstructions: v }))}
                        onDeliveryDateTimeChange={(v) => setFormData((d) => ({ ...d, deliveryDateTime: v }))}
                        isTransportCompany={isTransportCompany}
                        addressSaved={formData.pickupAddressSaved}
                        onSaveSuccess={() => setFormData((d) => ({ ...d, pickupAddressSaved: true }))}
                    />
                );
            case 1:
                return (
                    <DeliveryAddressStep
                        deliveryId={deliveryId}
                        customerIdForApi={deliveryCustomerId}
                        address={formData.deliveryAddress}
                        onChange={(a) => setFormData((d) => ({ ...d, deliveryAddress: a }))}
                        pickupDateTime={formData.pickupDateTime}
                        deliveryDateTime={formData.deliveryDateTime}
                        deliveryInstructions={formData.deliveryInstructions}
                        onDeliveryDateTimeChange={(v) => setFormData((d) => ({ ...d, deliveryDateTime: v }))}
                        onDeliveryInstructionsChange={(v) => setFormData((d) => ({ ...d, deliveryInstructions: v }))}
                        recipientId={formData.recipientId}
                        recipientName={formData.recipientName}
                        recipientDocument={formData.recipientDocument}
                        recipientPhone={formData.recipientPhone}
                        onResponsibleSelect={(r) => setFormData((d) => ({
                            ...d,
                            recipientId: r?.id ?? null,
                            recipientName: r?.name ?? '',
                            recipientDocument: r?.document ?? '',
                            recipientPhone: r?.phone ?? r?.phoneNumber ?? '',
                        }))}
                        onClearResponsible={() => setFormData((d) => ({
                            ...d,
                            recipientId: null,
                            recipientName: '',
                            recipientDocument: '',
                            recipientPhone: '',
                        }))}
                        customerId={statusPatchCustomerId}
                        isTransportCompany={isTransportCompany}
                        addressSaved={formData.deliveryAddressSaved}
                        onSaveSuccess={() => setFormData((d) => ({ ...d, deliveryAddressSaved: true }))}
                    />
                );
            case 2:
                return (
                    <ItemsStep
                        items={formData.items}
                        onChange={(items) => setFormData((d) => ({ ...d, items }))}
                        deliveryId={deliveryId}
                        customerIdForApi={deliveryCustomerId}
                    />
                );
            case 3:
                return isTransportCompany ? (
                    <ValoresStep
                        deliveryId={deliveryId}
                        customerIdForApi={deliveryCustomerId}
                        deliveryEntityCustomerId={formData.deliveryEntityCustomerId}
                        customerType={resolvedCustomerType}
                        driverId={formData.driverId}
                        driverName={formData.driverName}
                        onDriverChange={({ driverId: dId, driverName: dName }) =>
                            setFormData((d) => ({ ...d, driverId: dId, driverName: dName ?? '' }))
                        }
                        deliveryValue={formData.deliveryValue}
                        deliveryBillDescription={formData.deliveryBillDescription}
                        deliveryBillId={formData.deliveryBillId}
                        billEditable={formData.deliveryBillEditable && canEditBilling}
                        savedDeliveryValue={formData.savedDeliveryValue}
                        savedDeliveryBillDescription={formData.savedDeliveryBillDescription}
                        fees={formData.fees}
                        onDeliveryValueChange={(v) => setFormData((d) => ({ ...d, deliveryValue: v }))}
                        onDeliveryBillDescriptionChange={(v) => setFormData((d) => ({ ...d, deliveryBillDescription: v }))}
                        onBillSaved={(id) => setFormData((d) => ({ ...d, deliveryBillId: id, savedDeliveryValue: d.deliveryValue, savedDeliveryBillDescription: d.deliveryBillDescription }))}
                        onFeesChange={(v) => setFormData((d) => ({ ...d, fees: v }))}
                        onFeeSaved={(index, feeId, savedSnapshot) => setFormData((d) => {
                            const next = [...(d.fees || [])];
                            if (next[index]) next[index] = { ...next[index], feeId, savedSnapshot: savedSnapshot ?? undefined };
                            return { ...d, fees: next };
                        })}
                        onFeeUpdated={(index, savedSnapshot) => setFormData((d) => {
                            const next = [...(d.fees || [])];
                            if (next[index]) next[index] = { ...next[index], savedSnapshot: savedSnapshot ?? undefined };
                            return { ...d, fees: next };
                        })}
                        isTransportCompany={isTransportCompany}
                        requesterId={requesterInfo.id}
                        requesterName={requesterInfo.name}
                        requesterEmail={requesterInfo.email}
                        onRequesterChange={handleRequesterUpdated}
                        dataRefreshKey={refetchNonce}
                    />
                ) : isLegalPersonUser ? (
                    <DeliveryRequesterSection
                        deliveryId={deliveryId}
                        customerId={deliveryCustomerId}
                        isTransportCompany={false}
                        requesterId={requesterInfo.id}
                        requesterName={requesterInfo.name}
                        requesterEmail={requesterInfo.email}
                        onRequesterChange={handleRequesterUpdated}
                    />
                ) : (
                    <RevisaoStep
                        pickupAddress={formData.pickupAddress}
                        deliveryAddress={formData.deliveryAddress}
                        items={formData.items}
                        pickupDateTime={formData.pickupDateTime}
                        pickupInstructions={formData.pickupInstructions}
                        deliveryDateTime={formData.deliveryDateTime}
                        deliveryInstructions={formData.deliveryInstructions}
                        deliveryValue={formData.deliveryValue}
                        deliveryBillDescription={formData.deliveryBillDescription}
                        fees={formData.fees}
                        recipientName={formData.recipientName}
                        recipientDocument={formData.recipientDocument}
                        recipientPhone={formData.recipientPhone}
                        driverId={formData.driverId}
                        driverName={formData.driverName}
                        isTransportCompany={isTransportCompany}
                    />
                );
            case 4:
                return isTransportCompany || isLegalPersonUser ? (
                    <RevisaoStep
                        pickupAddress={formData.pickupAddress}
                        deliveryAddress={formData.deliveryAddress}
                        items={formData.items}
                        pickupDateTime={formData.pickupDateTime}
                        pickupInstructions={formData.pickupInstructions}
                        deliveryDateTime={formData.deliveryDateTime}
                        deliveryInstructions={formData.deliveryInstructions}
                        deliveryValue={formData.deliveryValue}
                        deliveryBillDescription={formData.deliveryBillDescription}
                        fees={formData.fees}
                        recipientName={formData.recipientName}
                        recipientDocument={formData.recipientDocument}
                        recipientPhone={formData.recipientPhone}
                        driverId={formData.driverId}
                        driverName={formData.driverName}
                        isTransportCompany={isTransportCompany}
                    />
                ) : null;
            default:
                return null;
        }
    };

    const isLastStep = currentStep === STEPS.length - 1;

    const showEmptyState = !hideSelector && isTransportCompany && !selectedCustomerId && !deliveryId;
    if (showEmptyState) {
        return (
            <PageWithCustomerSelector
                selectorFirstOptionLabel="Todas as empresas"
                selectorFirstOptionDescription="Gerenciar todas as entregas em aberto"
                includePhysicalCustomers
            >
                <Container>
                    <p>Selecione uma empresa no seletor acima para criar uma nova entrega.</p>
                    <Button variant="outline" onClick={() => navigate(ROUTES.ENTREGAS)}>
                        Voltar para entregas
                    </Button>
                </Container>
            </PageWithCustomerSelector>
        );
    }

    const needRequesterModal = !hideSelector && isTransportCompany && selectedCustomerId;
    if (needRequesterModal) {
        return (
            <PageWithCustomerSelector
                selectorFirstOptionLabel="Todas as empresas"
                selectorFirstOptionDescription="Gerenciar todas as entregas em aberto"
                includePhysicalCustomers
            >
                <Container>
                    <p style={{ marginBottom: '16px' }}>Selecione o solicitante da entrega para continuar.</p>
                </Container>
                <RequestersModal
                    customerId={selectedCustomerId}
                    onSelect={handleRequesterSelect}
                    onClose={() => navigate(ROUTES.ENTREGAS)}
                />
            </PageWithCustomerSelector>
        );
    }
    
    const finishDeliveryText = isTransportCompany ? 'Agendar entrega' : 'Solicitar entrega';
    // Cliente convencional só pode solicitar quando a entrega ainda está em criação.
    const showFinishButton =
        isTransportCompany || !deliveryState || deliveryState === 'CREATION';

    const requesterFillStepTarget = currentStep === 0 ? 'pickup' : currentStep === 1 ? 'delivery' : null;
    const renderRequesterFillButton = (target) => (
        canFillFromRequester ? (
            <Button
                type="button"
                variant="outline"
                onClick={() => fillAddressFromRequester(target)}
                disabled={requesterFillTarget != null}
                style={{ marginLeft: 'auto', justifyContent: 'center', textAlign: 'center' }}
            >
                {requesterFillTarget != null ? 'Buscando endereço...' : 'Usar endereço do solicitante'}
            </Button>
        ) : null
    );
    const stepHeaderAction = requesterFillStepTarget ? renderRequesterFillButton(requesterFillStepTarget) : null;

    const mainContent = (
        <>
            {messageModalEl}
            <Container>
            {showCancelModal && (
                <ConfirmCancelDeliveryModal
                    onConfirm={handleConfirmCancel}
                    onClose={() => !cancellingDelivery && setShowCancelModal(false)}
                    isSubmitting={cancellingDelivery}
                />
            )}
            <DeliveryCreationStepper
                steps={STEPS}
                currentStep={currentStep}
                onStepClick={setCurrentStep}
                onCancel={() => setShowCancelModal(true)}
                deliveryId={deliveryId}
            />
            <FormContainer as="div">
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                        marginBottom: '24px',
                    }}
                >
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#222', minWidth: 0 }}>
                        {STEPS[currentStep]}
                    </h2>
                    {stepHeaderAction}
                </div>
                {renderStepContent()}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', flexWrap: 'wrap', gap: '12px' }}>
                    <Button variant="outline" onClick={goBack} disabled={currentStep === 0} type="button">
                        Voltar
                    </Button>
                    {showFinishButton && (
                        <Button
                            variant="primary"
                            type="button"
                            disabled={creating || hydratingDelivery}
                            onClick={async () => {
                                if (isLastStep) {
                                    if (!deliveryId) {
                                        setMessageModal({
                                            open: true,
                                            title: 'Aviso',
                                            message: 'ID da entrega não encontrado.',
                                        });
                                        return;
                                    }
                                    try {
                                        await deliveryService.requestDelivery(deliveryId, deliveryCustomerId);
                                        window.location.replace(ROUTES.ENTREGAS);
                                    } catch (err) {
                                        setMessageModal({
                                            open: true,
                                            title: 'Erro',
                                            message: err?.message || 'Erro ao solicitar/agendar a entrega.',
                                        });
                                    }
                                    return;
                                }
                                await goNext();
                            }}
                        >
                            {isLastStep ? finishDeliveryText : 'Próximo'}
                        </Button>
                    )}
                </div>
            </FormContainer>
        </Container>
        </>
    );

    if (hideSelector) {
        // Abertura via detalhe /entregas/:id
        if (!deliveryId || !deliveryState) {
            return (
                <>
                    {messageModalEl}
                    <Container>
                        <p style={{ padding: '24px 0', color: '#555' }}>Carregando entrega…</p>
                    </Container>
                </>
            );
        }

        const normalizedDeliveryState = normalizeDeliveryStateFromApi(deliveryState);
        const isCreateMode = normalizedDeliveryState === DELIVERY_STATUS.CREATION
            || normalizedDeliveryState === DELIVERY_STATUS.CONFIRMATION;
        const isDeliveryContentLocked =
            normalizedDeliveryState === DELIVERY_STATUS.CANCELLED
            || normalizedDeliveryState === DELIVERY_STATUS.DELIVERED;
        const canEditInConsult = isTransportCompany && !isCreateMode;

        const consultHeaderActions = (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '8px', flexShrink: 0 }}>
                {canPrintDeliveryProtocol ? (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleOpenProtocolPrintModal}
                        disabled={printingProtocol || hydratingDelivery}
                        style={{ justifyContent: 'center', whiteSpace: 'nowrap' }}
                    >
                        <PrintIcon />
                        Imprimir protocolo
                    </Button>
                ) : null}
                <CopyDeliverySummaryButton
                    deliveryId={deliveryId}
                    deliveryState={deliveryState}
                    customerName={deliveryCustomer.name}
                    customerDocument={deliveryCustomer.document}
                    pickupAddress={formData.pickupAddress}
                    pickupInstructions={formData.pickupInstructions}
                    deliveryAddress={formData.deliveryAddress}
                    deliveryInstructions={formData.deliveryInstructions}
                    recipientName={formData.recipientName}
                    recipientDocument={formData.recipientDocument}
                    recipientPhone={formData.recipientPhone}
                    items={formData.items}
                    disabled={hydratingDelivery}
                />
            </div>
        );

        const consultValoresSection = isTransportCompany ? (
            <section style={{ marginBottom: '32px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#111827' }}>
                    Detalhes da entrega
                </h3>
                <ValoresStep
                    deliveryId={deliveryId}
                    customerIdForApi={deliveryCustomerId}
                    deliveryEntityCustomerId={formData.deliveryEntityCustomerId}
                    customerType={resolvedCustomerType}
                    driverId={formData.driverId}
                    driverName={formData.driverName}
                    onDriverChange={({ driverId: dId, driverName: dName }) =>
                        setFormData((d) => ({ ...d, driverId: dId, driverName: dName ?? '' }))
                    }
                    deliveryValue={formData.deliveryValue}
                    deliveryBillDescription={formData.deliveryBillDescription}
                    deliveryBillId={formData.deliveryBillId}
                    billEditable={formData.deliveryBillEditable && canEditBilling}
                    savedDeliveryValue={formData.savedDeliveryValue}
                    savedDeliveryBillDescription={formData.savedDeliveryBillDescription}
                    fees={formData.fees}
                    onDeliveryValueChange={(v) => setFormData((d) => ({ ...d, deliveryValue: v }))}
                    onDeliveryBillDescriptionChange={(v) => setFormData((d) => ({ ...d, deliveryBillDescription: v }))}
                    onBillSaved={(id) => setFormData((d) => ({
                        ...d,
                        deliveryBillId: id,
                        savedDeliveryValue: d.deliveryValue,
                        savedDeliveryBillDescription: d.deliveryBillDescription,
                    }))}
                    onFeesChange={(v) => setFormData((d) => ({ ...d, fees: v }))}
                    onFeeSaved={(index, feeId, savedSnapshot) => setFormData((d) => {
                        const next = [...(d.fees || [])];
                        if (next[index]) next[index] = { ...next[index], feeId, savedSnapshot: savedSnapshot ?? undefined };
                        return { ...d, fees: next };
                    })}
                    onFeeUpdated={(index, savedSnapshot) => setFormData((d) => {
                        const next = [...(d.fees || [])];
                        if (next[index]) next[index] = { ...next[index], savedSnapshot: savedSnapshot ?? undefined };
                        return { ...d, fees: next };
                    })}
                    isTransportCompany={isTransportCompany}
                    requesterId={requesterInfo.id}
                    requesterName={requesterInfo.name}
                    requesterEmail={requesterInfo.email}
                    onRequesterChange={handleRequesterUpdated}
                    dataRefreshKey={refetchNonce}
                />
            </section>
        ) : null;

        if (isCreateMode) {
            return mainContent;
        }

        // CONSULTA – clientes (somente leitura)
        if (!canEditInConsult) {
            return (
                <>
                    {messageModalEl}
                    {protocolPrintModal}
                    <Container>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '16px',
                            marginBottom: '16px',
                        }}
                    >
                        <h2 style={{ margin: 0, fontSize: '18px', color: '#222' }}>
                            Protocolo da entrega
                        </h2>
                        {consultHeaderActions}
                    </div>
                    <p style={{ color: '#555', lineHeight: 1.55, marginBottom: '16px', maxWidth: '40rem' }}>
                        Você está visualizando o resumo desta entrega. Os dados abaixo representam o protocolo
                        da solicitação.
                    </p>
                    {canViewAssociatedRoutes ? (
                        <DeliveryAssociatedRoutesInfo routes={deliveryRoutes} />
                    ) : null}
                    {isTransportCompany ? (
                        <DeliveryStatusUpdateSection
                            deliveryId={deliveryId}
                            currentState={deliveryState}
                            customerIdForApi={statusPatchCustomerId}
                            history={deliveryHistory}
                            deliveryRoutes={deliveryRoutes}
                            delayed={deliveryDelayed}
                            onStatusUpdated={bumpDeliveryRefetch}
                        />
                    ) : deliveryDelayed ? (
                        <TimelineSection>
                            <TimelineTitleRow>
                                <TimelineTitle>Status da entrega</TimelineTitle>
                                <DeliveryDelayedBadge delayed />
                            </TimelineTitleRow>
                            {deliveryState ? (
                                <DeliveryStatusBadge state={deliveryState} compact />
                            ) : null}
                        </TimelineSection>
                    ) : null}
                    <DeliveryStatusTimeline
                        history={deliveryHistory}
                        deliveryId={deliveryId}
                        customerIdForApi={statusPatchCustomerId}
                        driverId={formData.driverId}
                        canManagePhotos={canManageDeliveryPhotos}
                        onPhotosChanged={bumpDeliveryRefetch}
                    />
                    <FormContainer as="div">
                        <RevisaoStep
                            pickupAddress={formData.pickupAddress}
                            deliveryAddress={formData.deliveryAddress}
                            items={formData.items}
                            pickupDateTime={formData.pickupDateTime}
                            pickupInstructions={formData.pickupInstructions}
                            deliveryDateTime={formData.deliveryDateTime}
                            deliveryInstructions={formData.deliveryInstructions}
                            deliveryValue={formData.deliveryValue}
                            deliveryBillDescription={formData.deliveryBillDescription}
                            fees={formData.fees}
                            recipientName={formData.recipientName}
                            recipientDocument={formData.recipientDocument}
                            recipientPhone={formData.recipientPhone}
                            driverId={formData.driverId}
                            driverName={formData.driverName}
                            isTransportCompany={isTransportCompany}
                        />
                        <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(ROUTES.ENTREGAS, { replace: true })}
                            >
                                Voltar para entregas
                            </Button>
                        </div>
                    </FormContainer>
                </Container>
                </>
            );
        }

        // CONSULTA EDITÁVEL – empresa de transporte
        return (
            <>
                {messageModalEl}
                {protocolPrintModal}
                <Container>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '16px',
                        marginBottom: '16px',
                    }}
                >
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#222' }}>
                        Consulta e edição da entrega
                    </h2>
                    {consultHeaderActions}
                </div>
                <p style={{ color: '#555', lineHeight: 1.55, marginBottom: '16px', maxWidth: '40rem' }}>
                    {isDeliveryContentLocked
                        ? (formData.deliveryBillEditable
                            ? (isLegalPersonCustomer(resolvedCustomerType)
                                ? 'Esta entrega está cancelada ou já foi entregue. Endereços e itens não podem ser alterados, mas valor, motorista, centro de custo e solicitante continuam editáveis.'
                                : 'Esta entrega está cancelada ou já foi entregue. Endereços e itens não podem ser alterados, mas valor, motorista e solicitante continuam editáveis.')
                            : (isLegalPersonCustomer(resolvedCustomerType)
                                ? 'Esta entrega está cancelada ou já foi entregue. Endereços, itens e valores não podem ser alterados, mas motorista, centro de custo e solicitante continuam editáveis.'
                                : 'Esta entrega está cancelada ou já foi entregue. Endereços, itens e valores não podem ser alterados, mas motorista e solicitante continuam editáveis.'))
                        : (formData.deliveryBillEditable
                            ? 'Você está visualizando o protocolo desta entrega. Como empresa de transporte, é possível atualizar os dados abaixo.'
                            : 'Você está visualizando o protocolo desta entrega. Os valores da cobrança não podem ser alterados. Demais dados editáveis continuam disponíveis.')}
                </p>
                {canViewAssociatedRoutes ? (
                    <DeliveryAssociatedRoutesInfo routes={deliveryRoutes} />
                ) : null}
                <DeliveryStatusUpdateSection
                    deliveryId={deliveryId}
                    currentState={deliveryState}
                    customerIdForApi={statusPatchCustomerId}
                    history={deliveryHistory}
                    deliveryRoutes={deliveryRoutes}
                    delayed={deliveryDelayed}
                    onStatusUpdated={bumpDeliveryRefetch}
                />
                <DeliveryStatusTimeline
                    history={deliveryHistory}
                    deliveryId={deliveryId}
                    customerIdForApi={statusPatchCustomerId}
                    driverId={formData.driverId}
                    canManagePhotos={canManageDeliveryPhotos}
                    onPhotosChanged={bumpDeliveryRefetch}
                />
                <FormContainer as="div">
                    {isDeliveryContentLocked ? (
                        <RevisaoStep
                            pickupAddress={formData.pickupAddress}
                            deliveryAddress={formData.deliveryAddress}
                            items={formData.items}
                            pickupDateTime={formData.pickupDateTime}
                            pickupInstructions={formData.pickupInstructions}
                            deliveryDateTime={formData.deliveryDateTime}
                            deliveryInstructions={formData.deliveryInstructions}
                            deliveryValue={formData.deliveryValue}
                            deliveryBillDescription={formData.deliveryBillDescription}
                            fees={formData.fees}
                            recipientName={formData.recipientName}
                            recipientDocument={formData.recipientDocument}
                            recipientPhone={formData.recipientPhone}
                            driverId={formData.driverId}
                            driverName={formData.driverName}
                            isTransportCompany={isTransportCompany}
                            showDriverSection={false}
                            showValuesSection={false}
                        />
                    ) : (
                        <>
                            <section style={{ marginBottom: '32px' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <h3 style={{ margin: 0, fontSize: '15px', color: '#111827', minWidth: 0 }}>
                                        Endereço de coleta
                                    </h3>
                                    {renderRequesterFillButton('pickup')}
                                </div>
                                <PickupAddressStep
                                    deliveryId={deliveryId}
                                    customerIdForApi={deliveryCustomerId}
                                    address={formData.pickupAddress}
                                    onChange={(a) => setFormData((d) => ({ ...d, pickupAddress: a }))}
                                    pickupDateTime={formData.pickupDateTime}
                                    pickupInstructions={formData.pickupInstructions}
                                    deliveryDateTime={formData.deliveryDateTime}
                                    onPickupDateTimeChange={(v) => setFormData((d) => ({ ...d, pickupDateTime: v }))}
                                    onPickupInstructionsChange={(v) => setFormData((d) => ({ ...d, pickupInstructions: v }))}
                                    onDeliveryDateTimeChange={(v) => setFormData((d) => ({ ...d, deliveryDateTime: v }))}
                                    isTransportCompany={isTransportCompany}
                                    addressSaved={formData.pickupAddressSaved}
                                    onSaveSuccess={() => setFormData((d) => ({ ...d, pickupAddressSaved: true }))}
                                />
                            </section>

                            <section style={{ marginBottom: '32px' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <h3 style={{ margin: 0, fontSize: '15px', color: '#111827', minWidth: 0 }}>
                                        Endereço de entrega e responsável
                                    </h3>
                                    {renderRequesterFillButton('delivery')}
                                </div>
                                <DeliveryAddressStep
                                    deliveryId={deliveryId}
                                    customerIdForApi={deliveryCustomerId}
                                    address={formData.deliveryAddress}
                                    onChange={(a) => setFormData((d) => ({ ...d, deliveryAddress: a }))}
                                    pickupDateTime={formData.pickupDateTime}
                                    deliveryDateTime={formData.deliveryDateTime}
                                    deliveryInstructions={formData.deliveryInstructions}
                                    onDeliveryDateTimeChange={(v) => setFormData((d) => ({ ...d, deliveryDateTime: v }))}
                                    onDeliveryInstructionsChange={(v) => setFormData((d) => ({ ...d, deliveryInstructions: v }))}
                                    recipientId={formData.recipientId}
                                    recipientName={formData.recipientName}
                                    recipientDocument={formData.recipientDocument}
                                    recipientPhone={formData.recipientPhone}
                                    onResponsibleSelect={(r) => setFormData((d) => ({
                                        ...d,
                                        recipientId: r?.id ?? null,
                                        recipientName: r?.name ?? '',
                                        recipientDocument: r?.document ?? '',
                                        recipientPhone: r?.phone ?? r?.phoneNumber ?? '',
                                    }))}
                                    onClearResponsible={() => setFormData((d) => ({
                                        ...d,
                                        recipientId: null,
                                        recipientName: '',
                                        recipientDocument: '',
                                        recipientPhone: '',
                                    }))}
                                    customerId={statusPatchCustomerId}
                                    isTransportCompany={isTransportCompany}
                                    addressSaved={formData.deliveryAddressSaved}
                                    onSaveSuccess={() => setFormData((d) => ({ ...d, deliveryAddressSaved: true }))}
                                />
                            </section>

                            <section style={{ marginBottom: '32px' }}>
                                <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#111827' }}>
                                    Itens da entrega
                                </h3>
                                <ItemsStep
                                    items={formData.items}
                                    onChange={(items) => setFormData((d) => ({ ...d, items }))}
                                    deliveryId={deliveryId}
                                    customerIdForApi={deliveryCustomerId}
                                />
                            </section>
                        </>
                    )}

                    {consultValoresSection}

                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(ROUTES.ENTREGAS, { replace: true })}
                        >
                            Voltar para entregas
                        </Button>
                    </div>
                </FormContainer>
            </Container>
            </>
        );
    }

    return (
        <PageWithCustomerSelector
            selectorFirstOptionLabel="Todas as empresas"
            selectorFirstOptionDescription="Gerenciar todas as entregas em aberto"
            selectorClientOnly={isTransportCompany && !selectedCustomerId}
            includePhysicalCustomers
        >
            {mainContent}
        </PageWithCustomerSelector>
    );
}
